import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import { parseIsoToEpochMs } from '../../../time-utils.ts';

function parseJson<T>(v: unknown): T | null {
  if (v == null) return null;
  if (typeof v === 'string') {
    try {
      return JSON.parse(v) as T;
    } catch {
      return null;
    }
  }
  return v as T;
}

// Season meta only — the in-game wave/enemy/shop data lives in
// `serverLeagueTables`, referenced by `leagueTableId`. Multiple seasons share
// the same league table (e.g. 6 regular-season schedule rows all point at
// the same regular-season table), so inlining wave data per season was a
// large duplication. See serverLeagueTables for the table-side schema.
export const LeagueSeasonSchema = defineSchema({
  name: 'serverLeagueSeasons',
  fields: [
    ['id', 'number'],
    ['startTime', 'number'], // epoch ms (was 'string')
    ['endTime', 'number'], // epoch ms (was 'string')
    ['rewardTableId', 'string'],
    ['leagueTableId', 'number'],
    ['repeatScoreMultiplier', 'number'],
    ['repeatScoreMaxCount', 'number'],
    ['seasonReward', { kind: 'dict', dict: 'delivery' }],
  ],
  lookupKeys: ['id'],
});

export interface RawLeagueSeasonRow {
  id: number;
  start_time: string;
  end_time: string;
  reward_table_id: string;
  league_table_id: number;
  repeat_score_multiplier?: number | null;
  repeat_score_max_count?: number | null;
  season_reward?: unknown;
}

export function leagueSeasonFromRaw(row: RawLeagueSeasonRow) {
  return {
    id: row.id,
    startTime: parseIsoToEpochMs(row.start_time),
    endTime: parseIsoToEpochMs(row.end_time),
    rewardTableId: row.reward_table_id,
    leagueTableId: row.league_table_id,
    repeatScoreMultiplier: row.repeat_score_multiplier ?? 0,
    repeatScoreMaxCount: row.repeat_score_max_count ?? 0,
    seasonReward: parseJson<Record<string, number>>(row.season_reward),
  };
}

registerCategorySchema('serverLeagueSeasons', LeagueSeasonSchema);
